import { create } from "zustand";
import { INDICATOR_IDS, type IndicatorId } from "../components/chartVocabulary";
import { INSTRUMENTS } from "../lib/instruments";
import { BANK_CONTEXT_LENGTH } from "../lib/presets";
import { trackModeSwitched } from "../lib/analytics";
import {
  TIMEFRAMES,
  type Horizon,
  type Mode,
  type RoundFilter,
  type Timeframe,
} from "../lib/rounds";
import { storage } from "../lib/storage";
import type { Preset } from "../types";

/**
 * Player settings (spec §4, §11 `settingsStore`) — mode, Predict timeframe,
 * difficulty preset, market subset, Context length, and default indicators.
 * Persisted to localStorage; changeable any time, in place. Stored mode values
 * from before ADR-0006 (`simple` / `nextCandle` / `advanced`) are migrated on
 * load, as are stored `indicators` entries that still hold KLineChart's own
 * names instead of our domain ids, and the retired `outcome` setting is
 * dropped (Predict is Higher/Lower only).
 */

export type Settings = {
  /** Active play mode (ADR-0006): Predict or Trade. */
  mode: Mode;
  /** Predict's timeframe: which candle size the Predict loop plays. */
  timeframe: Timeframe;
  /** Predict's guess horizon: next candle green/red, or the barrier trend. */
  horizon: Horizon;
  preset: Preset;
  /** Instrument ids to draw from; empty = all markets. */
  markets: string[];
  /** How many Context candles to show (≤ 50). */
  contextLength: number;
  /** Indicator ids on by default (consumed by the TA workbench, GRA-83). */
  indicators: IndicatorId[];
};

export const DEFAULT_SETTINGS: Settings = {
  mode: "predict",
  timeframe: "daily",
  horizon: "trend",
  preset: "normal",
  markets: [],
  contextLength: 50,
  indicators: [],
};

const SETTINGS_KEY = "settings";

export interface SettingsState extends Settings {
  setMode: (mode: Mode) => void;
  setTimeframe: (timeframe: Timeframe) => void;
  setHorizon: (horizon: Horizon) => void;
  setPreset: (preset: Preset) => void;
  /**
   * Toggle a whole market group on or off (see the method body for the
   * fill-in-first rule). Refuses to turn off the last remaining explicit
   * group (GRA-129): returns false and leaves `markets` untouched, so the
   * caller can flash a "choose at least one market" message rather than the
   * old behavior of silently falling back to the all-markets state.
   *
   * `pool` is every instrument currently on offer. Pass it and a chip lit by
   * the implicit all-markets state (`markets: []`) toggles *off* against that
   * pool, which is the whole point of GRA-148: without it, clicking a lit chip
   * narrowed the selection to that group alone, so the same click meant
   * "only this" on one chip and "not this" on the next.
   */
  toggleMarkets: (instrumentIds: string[], pool?: string[]) => boolean;
  /**
   * Explicit "All" control (GRA-129): selects the full set from any subset.
   * Turning it off from the all-selected state would leave zero markets
   * selected, so that direction is refused the same way as the last group
   * deselect — returns false and leaves `markets` untouched.
   */
  toggleAllMarkets: () => boolean;
  setContextLength: (length: number) => void;
  setIndicators: (indicators: IndicatorId[]) => void;
  reset: () => void;
}

/**
 * Legacy KLineChart names that once leaked into stored `indicators` entries
 * (pre-domain-id seam fix), mapped to today's domain ids. Any stored entry not
 * in this table and not already a current `IndicatorId` is unrecognized and
 * dropped, so a future KLineChart rename cannot silently corrupt this list.
 */
const LEGACY_INDICATOR_NAMES: Record<string, IndicatorId> = {
  MA: "sma",
  EMA: "ema",
  BOLL: "bbands",
  RSI: "rsi",
  MACD: "macd",
  VOL: "vol",
};

const CURRENT_INDICATOR_IDS: ReadonlySet<string> = new Set(INDICATOR_IDS);

/** Map one stored `indicators` entry to a current domain id, or drop it. */
function migrateIndicator(entry: unknown): IndicatorId | undefined {
  if (typeof entry !== "string") return undefined;
  if (CURRENT_INDICATOR_IDS.has(entry)) return entry as IndicatorId;
  return LEGACY_INDICATOR_NAMES[entry];
}

const TIMEFRAME_VALUES: ReadonlySet<string> = new Set<string>(TIMEFRAMES);

/**
 * Map a stored settings blob onto the current shape (ADR-0006 migration):
 * pre-two-modes values `simple` → `predict`, `nextCandle` → `predict` with
 * timeframe `weekly`, `advanced` → `trade`; any unrecognized mode or timeframe
 * falls back to the defaults. Stored `indicators` entries holding KLineChart's
 * own names are mapped to domain ids (`LEGACY_INDICATOR_NAMES`); unrecognized
 * entries are dropped rather than carried through. All other stored fields
 * pass through untouched.
 *
 * The guess horizon arrived after the timeframe row, so a blob saved before it
 * has none: those are read as the game that timeframe *was*. Weekly was the
 * next-candle game and nothing else, so it migrates to `nextCandle`; daily was
 * the barrier game, so it takes the default `trend`.
 */
export function migrateStoredSettings(
  stored: Partial<Record<string, unknown>>,
): Partial<Settings> {
  const { mode, timeframe, horizon, indicators, markets, ...rest } = stored;
  const migrated = { ...rest } as Partial<Settings>;

  // An instrument that has left the pool (the bond futures were dropped) must
  // not linger in the stored narrowing: a selection made only of retired ids
  // would filter every Round out, and the chips could never clear it.
  if (Array.isArray(markets)) {
    migrated.markets = markets.filter(
      (id): id is string => typeof id === "string" && INSTRUMENTS.some((i) => i.id === id),
    );
  }

  if (typeof timeframe === "string" && TIMEFRAME_VALUES.has(timeframe)) {
    migrated.timeframe = timeframe as Timeframe;
  }
  if (mode === "predict" || mode === "trade" || mode === "challenge") {
    migrated.mode = mode;
  } else if (mode === "simple") {
    migrated.mode = "predict";
  } else if (mode === "nextCandle") {
    migrated.mode = "predict";
    migrated.timeframe = "weekly";
  } else if (mode === "advanced") {
    migrated.mode = "trade";
  } else {
    // Unknown mode: drop it (and any timeframe it implied) → defaults.
    delete migrated.mode;
  }

  if (horizon === "trend" || horizon === "nextCandle") {
    migrated.horizon = horizon;
  } else if (migrated.timeframe === "weekly") {
    migrated.horizon = "nextCandle";
  }

  if (Array.isArray(indicators)) {
    migrated.indicators = indicators
      .map(migrateIndicator)
      .filter((id): id is IndicatorId => id !== undefined);
  }

  return migrated;
}

/** Merge stored settings (migrated) over defaults so new fields get sane values. */
export function loadSettings(): Settings {
  return {
    ...DEFAULT_SETTINGS,
    ...migrateStoredSettings(storage.get<Partial<Record<string, unknown>>>(SETTINGS_KEY, {})),
  };
}

/**
 * How many Context candles to demand of a Round on `timeframe`, given the
 * player's flat setting. Clamped to what that bank actually stores: the
 * setting is a number of candles on every timeframe (ADR-0013), and a weekly
 * bank holds 40, so an unclamped 50 would empty the pool and fall back with a
 * warning on every pick rather than simply serving the whole Context.
 */
function contextRequirement(timeframe: Timeframe): number {
  const { contextLength } = useSettingsStore.getState();
  return Math.min(contextLength, BANK_CONTEXT_LENGTH[timeframe]);
}

/**
 * The eligible-Round filter Trade builds from the current settings (GRA-90).
 * The Predict trend Loop uses `trendRoundFilter` instead, which adds the
 * barrier requirement (GRA-140); the Next-Candle Loops use
 * `marketsRoundFilter`: Context length is not theirs to set.
 */
export function settingsRoundFilter(timeframe: Timeframe = "daily"): RoundFilter {
  const { markets } = useSettingsStore.getState();
  return {
    instruments: markets.length > 0 ? markets : undefined,
    minContextLength: contextRequirement(timeframe),
  };
}

/**
 * The eligible-Round filter the Predict trend Loop builds from the current
 * settings (GRA-140): the market/Context-length filter plus the active
 * difficulty preset, so only a Round whose resolution at that preset actually
 * hits a barrier is served. Product decision: a trend Round that would only
 * resolve by the time cap is never played, so the "if neither is hit, the net
 * move's sign decides" case never reaches the player. Re-read at every pick,
 * so a preset switch changes which Rounds are eligible for the very next one.
 */
export function trendRoundFilter(timeframe: Timeframe = "daily"): RoundFilter {
  const { markets, preset } = useSettingsStore.getState();
  return {
    instruments: markets.length > 0 ? markets : undefined,
    minContextLength: contextRequirement(timeframe),
    barrierPreset: preset,
  };
}

/**
 * The market-only filter, for the Next-Candle Loop over the daily barrier
 * bank: it shows the whole stored Context, so the Context-length setting is
 * disabled for it and must not narrow the pool either.
 */
export function marketsRoundFilter(): RoundFilter {
  const { markets } = useSettingsStore.getState();
  return { instruments: markets.length > 0 ? markets : undefined };
}

export const useSettingsStore = create<SettingsState>((set, get) => {
  const persist = () => {
    const { mode, timeframe, horizon, preset, markets, contextLength, indicators } =
      get();
    storage.set(SETTINGS_KEY, {
      mode,
      timeframe,
      horizon,
      preset,
      markets,
      contextLength,
      indicators,
    });
  };
  const update = (patch: Partial<Settings>) => {
    set(patch);
    persist();
  };

  return {
    ...loadSettings(),

    setMode: (mode) => {
      // Every switcher destination reports now (GRA-149): Predict, Trade and
      // Challenge alike, so the funnel into the Daily challenge is visible
      // too, not only Predict/Trade.
      if (mode !== get().mode) {
        trackModeSwitched({ to: mode });
      }
      update({ mode });
    },
    setTimeframe: (timeframe) => update({ timeframe }),
    setHorizon: (horizon) => update({ horizon }),
    setPreset: (preset) => update({ preset }),
    /**
     * Turn a whole market group on or off in one click. Off only when every one
     * of its instruments is already selected: a partly-selected group fills in
     * the rest rather than clearing, which is what a half-lit chip invites.
     *
     * "Already selected" is read against `pool` when the stored list is empty,
     * since an empty list means every market is in play and every chip is lit
     * (GRA-148). So a lit chip always turns its group off, whether the state
     * behind it was explicit or implicit, and the click that lands on the last
     * lit group is refused rather than emptying the list.
     */
    toggleMarkets: (instrumentIds, pool) => {
      const stored = get().markets;
      const current = stored.length > 0 ? stored : (pool ?? []);
      const next = instrumentIds.every((id) => current.includes(id))
        ? current.filter((id) => !instrumentIds.includes(id))
        : [...current, ...instrumentIds.filter((id) => !current.includes(id))];
      // Turning off the sole remaining group would drop `markets` to [], which
      // reads as "all markets" everywhere else (GRA-129: that silent flip back
      // to all is the bug). Refuse it instead. Filling in a partial group never
      // lands on [], so it is never blocked.
      if (next.length === 0) return false;
      update({ markets: next });
      return true;
    },
    toggleAllMarkets: () => {
      const { markets } = get();
      // Already every market: turning "All" off has nowhere to land but zero
      // markets selected, so refuse it (same "keep at least one" rule).
      if (markets.length === 0) return false;
      update({ markets: [] });
      return true;
    },
    setContextLength: (contextLength) => update({ contextLength }),
    setIndicators: (indicators) => update({ indicators }),
    reset: () => update({ ...DEFAULT_SETTINGS }),
  };
});
